

import React, { useState, useEffect } from 'react';
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';

const emptyGuid = '00000000-0000-0000-0000-000000000000';

const initialFieldValues = {
    productImageId: emptyGuid,
    productId: '',
    imageFile: null,
    imageSrc: ''
};

const ProductImage = () => {
    const [lists, setLists] = useState([]);
    const [products, setProducts] = useState([]);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});

    const THEME_COLOR = '#065f46';

    useEffect(() => {
        GetData();
        GetProducts();
    }, []);

    const GetProducts = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLPRODUCTS);
            setProducts(res.data || []);
        } catch (error) {
            console.error("Error fetching products", error);
        }
    };

    const GetData = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLPRODUCTIMAGES);
            setLists(res.data || []);
        } catch (error) {
            console.error("Error fetching product images", error);
        }
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
    };

    const showPreview = (e) => {
        if (e.target.files && e.target.files[0]) {
            const imageFile = e.target.files[0];
            const reader = new FileReader();
            reader.onload = (x) => {
                setValues({ ...values, imageFile, imageSrc: x.target.result });
            };
            reader.readAsDataURL(imageFile);
        }
    };

    const validate = () => {
        let temp = {};
        temp.productId = values.productId && values.productId !== "" ? "" : "Product is required";
        if (values.productImageId === emptyGuid) {
            temp.imageFile = values.imageFile ? "" : "Image is required";
        }
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setBtnLoading(true);
        const formData = new FormData();
        // Match exactly with ProductImageRequest.cs properties
        formData.append('ProductImageId', values.productImageId);
        formData.append('ProductId', values.productId);
        if (values.imageFile) {
            formData.append('ImageFile', values.imageFile);
        }

        try {
            if (values.productImageId === emptyGuid) {
                await axios.post(config.APIACTIVEURL + config.CREATEPRODUCTIMAGE, formData);
            } else {
                await axios.put(config.APIACTIVEURL + config.UPDATEPRODUCTIMAGE, formData);
            }
            setShowModal(false);
            resetForm();
            GetData();
        } catch (error) {
            console.error("Save failed", error.response?.data || error.message);
        } finally {
            setBtnLoading(false);
        }
    };

    const handleEdit = (item) => {
        setValues({
            productImageId: item.productImageId,
            productId: item.productId,
            imageFile: null,
            imageSrc: item.imageSrc
        });
        setErrors({});
        setShowModal(true);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(config.APIACTIVEURL + config.DELETEPRODUCTIMAGE + "/" + itemToDelete);
            setShowDeleteModal(false);
            GetData();
        } catch (error) {
            console.error("Delete failed", error);
        }
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setErrors({});
    };

    return (
        <div className="page-content" style={{ paddingTop: "80px" }}>
            <div className="container-fluid py-4">
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <h4 className="fw-bold mb-0" style={{ color: '#064e3b' }}>Product Images</h4>
                        <button className="btn text-white fw-bold px-4 shadow-sm" style={{ backgroundColor: THEME_COLOR }} onClick={() => { resetForm(); setShowModal(true); }}>
                            <i className="ri-add-line me-1"></i> Add Image
                        </button>
                    </div>

                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr className="text-secondary small text-uppercase">
                                        <th style={{ width: '120px' }}>Image</th>
                                        <th>Product</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lists.map((item) => {
                                        // Handle the prodcutId typo from the products array
                                        const product = products.find(p => (p.prodcutId || p.productId) === item.productId);
                                        return (
                                            <tr key={item.productImageId}>
                                                <td>
                                                    <img src={item.imageSrc} alt="" className="rounded shadow-sm" style={{ width: '45px', height: '45px', objectFit: 'cover' }} />
                                                </td>
                                                <td className="fw-bold text-dark">{product?.productName || 'Unknown Product'}</td>
                                                <td className="text-center">
                                                    <button className="btn btn-sm btn-outline-primary border-0 me-2" onClick={() => handleEdit(item)}><i className="ri-pencil-line fs-5"></i></button>
                                                    <button className="btn btn-sm btn-outline-danger border-0" onClick={() => { setItemToDelete(item.productImageId); setShowDeleteModal(true); }}><i className="ri-delete-bin-line fs-5"></i></button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold">{values.productImageId === emptyGuid ? "Add Image" : "Edit Image"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="px-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label small fw-bold text-muted">Product</label>
                                <select name="productId" value={values.productId} onChange={handleInput} className={`form-select ${errors.productId ? 'is-invalid' : ''}`}>
                                    <option value="">Select Product</option>
                                    {products.map(p => (
                                        // FIX: Ensure key and value are not undefined
                                        <option key={p.prodcutId || p.productId} value={p.prodcutId || p.productId}>
                                            {p.productName}
                                        </option>
                                    ))}
                                </select>
                                {errors.productId && <div className="invalid-feedback">{errors.productId}</div>}
                            </div>

                            <div className="mb-4 text-center">
                                <div className="mx-auto mb-2" style={{ width: '100px', height: '100px', border: '2px dashed #ccc', borderRadius: '15px', overflow: 'hidden' }}>
                                    {values.imageSrc ? <img src={values.imageSrc} className="w-100 h-100" style={{ objectFit: 'cover' }} alt="preview" /> : <div className="h-100 d-flex align-items-center justify-content-center text-muted"><i className="ri-image-add-line fs-1"></i></div>}
                                </div>
                                <input type="file" accept="image/*" onChange={showPreview} className={`form-control form-control-sm ${errors.imageFile ? 'is-invalid' : ''}`} />
                                {errors.imageFile && <div className="invalid-feedback">{errors.imageFile}</div>}
                            </div>

                            <div className="modal-footer border-0 p-0 mt-4">
                                <button type="button" className="btn btn-light px-4 fw-bold" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" disabled={btnLoading} className="btn text-white px-5 fw-bold shadow-sm" style={{ backgroundColor: THEME_COLOR }}>
                                    {btnLoading ? 'Saving...' : 'Save Image'}
                                </button>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>
                
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton><Modal.Title>Delete Image</Modal.Title></Modal.Header>
                    <Modal.Body>Are you sure you want to delete this image?</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                        <Button variant="danger" onClick={handleDelete}>Delete</Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default ProductImage;
